import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const ALL_PERMS = [
  "pos","orders","tables","menu","inventory","vendors","purchases","customers","reports",
  "reconcile","day_end","transfers","areas","terminals","channels","delivery","reservations",
  "tasks","attendance","settings","edc","waiter","kitchen","kds","calls","chain","accounting","printers","whatsapp","brands","aggregator","mess","sms","*",
];
const ROLES = ["admin", "manager", "staff", "waiter", "kitchen"];

export default function Permissions() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["permissions"], queryFn: async () => (await api.get("/permissions")).data });
  const [matrix, setMatrix] = useState({});

  useEffect(() => {
    if (data?.effective) setMatrix(data.effective);
  }, [data]);

  const toggle = (role, perm) => {
    setMatrix((m) => {
      const cur = new Set(m[role] || []);
      if (cur.has(perm)) cur.delete(perm); else cur.add(perm);
      return { ...m, [role]: [...cur] };
    });
  };

  const save = async () => {
    try {
      await api.put("/permissions", { permissions: matrix });
      toast.success("Permissions saved");
      qc.invalidateQueries({ queryKey: ["permissions"] });
      qc.invalidateQueries({ queryKey: ["permissions-me"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="permissions">
      <PageHeader title="Staff permissions" subtitle="Fine-grained rights by role (PetPooja-style)">
        <Button onClick={save} className="bg-[var(--kul-primary)] text-white">Save matrix</Button>
      </PageHeader>
      <div className="overflow-x-auto kul-card">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="p-3">Permission</th>
              {ROLES.map((r) => <th key={r} className="p-3 capitalize">{r}</th>)}
            </tr>
          </thead>
          <tbody>
            {ALL_PERMS.map((perm) => (
              <tr key={perm} className="border-b border-neutral-100">
                <td className="p-3 font-mono text-xs">{perm}</td>
                {ROLES.map((role) => (
                  <td key={role} className="p-3">
                    <Checkbox
                      checked={(matrix[role] || []).includes(perm) || (matrix[role] || []).includes("*")}
                      onCheckedChange={() => toggle(role, perm)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
